import React, { FC, ReactNode, useEffect, useId, useCallback } from "react";
import styles from "./Modal.module.css";
import { Button } from "../../controls/Button/Button";

interface SubmitButtonConfig {
  onSubmit: () => void;
  title: string;
  isPending?: boolean;
  disabled?: boolean;
}

type PopUpSize = "big" | "normal";

interface ModalProps {
  children: ReactNode;

  /** Controlled open state */
  show: boolean;
  setShow: (show: boolean) => void;

  /** Optional header */
  title?: string;
  topLeftChildren?: ReactNode;

  /** Optional primary action button */
  submitButton?: SubmitButtonConfig;

  /** Additional action buttons rendered before the primary button */
  actionButtons?: ReactNode;

  /** Visual */
  size?: PopUpSize;
  className?: string;
  buttonsClassName?: string;

  /**
   * Auto close after N seconds.
   * - undefined / 0 => disabled
   * - -1 => never auto close (explicit)
   */
  disappear?: number;

  /** If true: render only content without header/footer/cross */
  empty?: boolean;

  /**
   * Callbacks
   * - onClose: fires when modal actually closes
   * - onCloseButtonClick: legacy compatibility (called on any close)
   */
  onClose?: () => void;
  onCloseButtonClick?: () => void;

  /** Accessibility */
  ariaLabel?: string;
}

const Modal: FC<ModalProps> = ({
  children,
  title,
  show,
  setShow,
  topLeftChildren,

  submitButton,
  actionButtons,

  size = "normal",
  disappear,
  empty = false,

  className,
  buttonsClassName,

  onClose,
  onCloseButtonClick,

  ariaLabel,
}) => {
  const titleId = useId();

  const close = useCallback(() => {
    setShow(false);
    onClose?.();
    onCloseButtonClick?.();
  }, [setShow, onClose, onCloseButtonClick]);

  // Auto-close
  useEffect(() => {
    if (!show) return;
    if (!disappear || disappear === 0 || disappear === -1) return;

    const timer = window.setTimeout(() => {
      close();
    }, disappear * 1000);

    return () => window.clearTimeout(timer);
  }, [show, disappear, close]);

  // Escape to close
  useEffect(() => {
    if (!show) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [show, close]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) close();
  };

  // Keep markup mounted to preserve animations if CSS relies on it
  return (
    <div
      className={`${styles.popUpContainer} ${show ? styles.show : ""}`}
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className={[
          styles.popUp,
          size === "big" ? styles.big : "",
          className ?? "",
        ].join(" ")}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
      >
        {empty ? (
          children
        ) : (
          <>
            {/* Header */}
            <div className="flex flex-row px-10 relative w-full justify-center">
              <div className="absolute left-0">{topLeftChildren}</div>
              {title && (
                <div id={titleId} className={styles.title}>
                  {title}
                </div>
              )}
            </div>

            {/* Body */}
            <div className={styles.body}>{children}</div>

            {/* Footer */}
            <div className={`${styles.footer} ${buttonsClassName ?? ""}`}>
              {actionButtons}
              {submitButton && (
                <Button
                  onClick={submitButton.onSubmit}
                  isLoading={submitButton.isPending}
                  disabled={submitButton.disabled}
                  fullWidth
                  className={styles.button}
                >
                  {submitButton.title}
                </Button>
              )}
            </div>

            {/* Close (X) */}
            <button
              type="button"
              onClick={close}
              className={styles.cross}
              aria-label="Close"
            >
              <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                <path
                  d="M4 4L16 16M16 4L4 16"
                  stroke="var(--color-primary)"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Modal;